import type { ContentTypeDefinition } from '../lib/flotiq-client/types/api-response';

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getEnv } from '../lib/env';
import { getContentType } from '../lib/flotiq-client';
import { getTimestamp } from '../lib/helpers';
import { getQueryCacheKey } from '../lib/queryClientHelpers';

import useSelectedSpace from './useSelectedSpace';
import useToken from './useToken';

const DEFAULT_CTD_UPDATE_CHECK_INTERVAL = 60 * 1000;

const getUpdateCheckInterval = () => {
  const interval = Number.parseInt(
    getEnv('VITE_CTD_UPDATE_CHECK_INTERVAL') || '',
    10,
  );

  return Number.isFinite(interval) && interval > 0
    ? interval
    : DEFAULT_CTD_UPDATE_CHECK_INTERVAL;
};

export type ContentTypeUpdateMonitorOptions = {
  enabled?: boolean;
  interval?: number | false;
  updatedAt?: string | null;
  onOutdated?: (updatedAt: string) => void;
};

export const useContentTypeUpdateMonitor = (
  contentType?: ContentTypeDefinition | null,
  options: ContentTypeUpdateMonitorOptions = {},
) => {
  const { enabled = true, interval, updatedAt, onOutdated } = options;
  const jwt = useToken();
  const { space } = useSelectedSpace();

  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState<string | null>(
    null,
  );
  const [isOutdated, setIsOutdated] = useState(false);
  const outdatedReportedRef = useRef(false);

  const currentContentTypeUpdatedAt = contentType
    ? contentType.updatedAt
    : null;

  const baselineTimestamp = useMemo(
    () => getTimestamp(baselineUpdatedAt),
    [baselineUpdatedAt],
  );

  const currentContentTypeTimestamp = useMemo(
    () => getTimestamp(currentContentTypeUpdatedAt),
    [currentContentTypeUpdatedAt],
  );

  useEffect(() => {
    setBaselineUpdatedAt(null);
    setIsOutdated(false);
    outdatedReportedRef.current = false;
  }, [contentType?.name, currentContentTypeUpdatedAt]);

  useEffect(() => {
    if (enabled) return;

    setBaselineUpdatedAt(null);
    setIsOutdated(false);
    outdatedReportedRef.current = false;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || baselineUpdatedAt) return;
    if (currentContentTypeTimestamp === null) return;

    setBaselineUpdatedAt(currentContentTypeUpdatedAt);
  }, [
    baselineUpdatedAt,
    currentContentTypeTimestamp,
    currentContentTypeUpdatedAt,
    enabled,
  ]);

  const params = useMemo(
    () => ({ contentType: contentType?.name }),
    [contentType],
  );

  const queryKey = useMemo(
    () =>
      getQueryCacheKey(
        'content-types-update-monitor',
        '',
        params,
        'entity',
        space,
      ),
    [params, space],
  );

  const shouldFetch =
    enabled &&
    !!contentType?.name &&
    !!jwt &&
    baselineTimestamp !== null &&
    !isOutdated;

  const { data: response } = useQuery({
    queryKey,
    queryFn: async ({ signal }) =>
      getContentType(
        jwt,
        space,
        { contentTypeName: contentType?.name },
        { signal },
      ),
    enabled: shouldFetch,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    retry: 0,
    refetchInterval: isOutdated
      ? false
      : (interval ?? getUpdateCheckInterval()),
    refetchIntervalInBackground: true,
  });

  const reportContentTypeUpdate = useCallback(
    (updatedAt?: string | null) => {
      const updatedTimestamp = getTimestamp(updatedAt);

      if (!enabled || isOutdated) return;
      if (baselineTimestamp === null || updatedTimestamp === null || !updatedAt)
        return;
      if (updatedTimestamp <= baselineTimestamp) return;
      if (outdatedReportedRef.current) return;

      outdatedReportedRef.current = true;
      setIsOutdated(true);
      onOutdated?.(updatedAt);
    },
    [baselineTimestamp, enabled, isOutdated, onOutdated],
  );

  useEffect(() => {
    reportContentTypeUpdate(updatedAt);
  }, [reportContentTypeUpdate, updatedAt]);

  useEffect(() => {
    reportContentTypeUpdate(response?.ok ? response.body?.updatedAt : null);
  }, [reportContentTypeUpdate, response?.body?.updatedAt, response?.ok]);

  return {
    isOutdated,
    baselineUpdatedAt,
  };
};

export default useContentTypeUpdateMonitor;
